import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import { logger } from '../logger';

type Namespace =
  | 'auth'
  | 'broadcast'
  | 'backstage'
  | 'devices'
  | 'natalie'
  | 'nattering'
  | 'event_wizard'
  | 'media_settings'
  | 'user'
  | 'video_service'
  | 'tech_check'
  | 'open_natter'
  | 'event'
  | 'reporting';

type Action = `${Namespace}.${string}` | `${Namespace}.${string}.${string}`;

/**
 * Tracker interface for tracking user interactions and setting user context.
 * Sets user context for both RUM and Logs.
 *
 * @see {@link https://docs.datadoghq.com/real_user_monitoring/guide/send-rum-custom-actions} for more information.
 */
export const monitoring = {
  trackAction: (action: Action, extra?: Record<string, unknown>) => {
    if (extra) {
      logger.info(`[monitoring.trackAction] ${action}`, extra);
      datadogRum.addAction(action, extra);
    } else {
      logger.info(`[monitoring.trackAction] ${action}`);
      datadogRum.addAction(action);
    }
  },
  setUser: (user: Parameters<typeof datadogRum.setUser>[0]) => {
    datadogRum.setUser(user);
    datadogLogs.setUser(user);
  },
  setUserProperty: (key: string, value: unknown) => {
    datadogRum.setUserProperty(key, value);
    datadogLogs.setUserProperty(key, value);
  },
  clearUser: () => {
    datadogRum.clearUser();
    datadogLogs.clearUser();
  },
  setContext: (key: string, value: unknown) => {
    datadogRum.setGlobalContextProperty(key, value);
    datadogLogs.setGlobalContextProperty(key, value);
  },
  removeContext: (key: string) => {
    datadogRum.removeGlobalContextProperty(key);
    datadogLogs.removeGlobalContextProperty(key);
  },
  /**
   * Reports an error to Datadog RUM for tracking and alerting.
   * @param error - The error to report
   * @param context - Additional context to attach to the error
   */
  reportError: (error: unknown, context?: Record<string, unknown>) => {
    datadogRum.addError(error, context);
  },
};
